import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ServerStackIcon,
  KeyIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

import apiService from '../services/api';
import { useOrganisation } from '../contexts/OrganisationContext';
import { McpCapability, McpGrant, McpStatus, McpToken, McpTokenIssued } from '../types/api';
import Logger from '../utils/logger';

/**
 * L'ecran du serveur MCP d'une organisation.
 *
 * CE QUE CET ECRAN DOIT RENDRE IMPOSSIBLE. Le jeton n'est rendu par l'API QU'UNE SEULE
 * FOIS, a sa creation : la base n'en garde qu'une empreinte, et aucun appel ne peut le
 * relire. Une interface qui l'affiche discretement dans une liste, ou qui laisse fermer
 * le panneau sans avoir averti, produit un jeton perdu et une organisation qui croit
 * l'avoir. D'ou un panneau dedie, un avertissement explicite, et un bouton de fermeture
 * qui dit « j'ai copie » plutot que « fermer ».
 *
 * CE QU'IL NE DOIT PAS SUGGERER. La capacite `destroy` n'existe pas dans le modele --
 * elle est absente, pas desactivee (docs/plan-mcp-nubiecloud.md §D1). L'ecran ne propose
 * donc que read / write / operate, et le dit : toute destruction passe par cette console.
 * Laisser entendre le contraire ferait chercher une case a cocher qui n'existera jamais.
 *
 * LES JETONS REVOQUES RESTENT LISTES. Les masquer donnerait une liste qui ne correspond
 * pas a ce que le serveur connait, et priverait d'une trace : `revoked_at` est ce qui
 * distingue « jamais emis » de « retire ».
 */

/**
 * `useTranslation` caste comme ailleurs dans ce depot : c'est la FONCTION qu'on caste.
 * Sans ca, l'inference generique de react-i18next fait deborder la limite de recursion
 * de TypeScript (TS2589). `i18next.d.ts` declare deja `resources: any`, donc le typage
 * des cles n'est garanti nulle part ici : ce cast ne renonce a rien.
 */
const traduire = useTranslation as unknown as (
  ns: string
) => { t: (cle: string, options?: Record<string, unknown>) => string };

const CAPACITES: McpCapability[] = ['read', 'write', 'operate'];

/** Un bouton de copie qui dit ce qu'il a fait -- sans quoi on ne sait pas si ca a pris. */
const BoutonCopier: React.FC<{ valeur: string; libelle: string; fait: string; large?: boolean }> = ({
  valeur,
  libelle,
  fait,
  large,
}) => {
  const [copie, setCopie] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(valeur);
          setCopie(true);
          window.setTimeout(() => setCopie(false), 2000);
        } catch (err) {
          // Le presse-papier est refuse hors contexte securise ou sans geste utilisateur.
          // On ne laisse pas l'utilisateur sans recours : le texte reste selectionnable.
          Logger.error('MCP: copie impossible', err);
        }
      }}
      className={`inline-flex items-center gap-1.5 ${
        large ? 'px-4 py-2.5 bg-accent text-white hover:bg-accent-hover' : 'px-2.5 py-1.5 bg-surface-2 text-text-secondary border border-border-1 hover:border-border-2'
      } rounded-button text-sm transition-colors`}
    >
      {copie ? <CheckIcon className="w-4 h-4" strokeWidth={2} /> : <ClipboardDocumentIcon className="w-4 h-4" strokeWidth={1.8} />}
      {copie ? fait : libelle}
    </button>
  );
};

const McpServerPage: React.FC = () => {
  const { t } = traduire('mcp');
  const { currentOrganisation } = useOrganisation();
  const orgId = currentOrganisation?.id;

  const [etat, setEtat] = useState<McpStatus | null>(null);
  const [jetons, setJetons] = useState<McpToken[]>([]);
  const [sessions, setSessions] = useState<McpGrant[]>([]);
  const [chargement, setChargement] = useState(true);
  const [echec, setEchec] = useState(false);

  const [label, setLabel] = useState('');
  const [capacites, setCapacites] = useState<McpCapability[]>(['read']);
  const [emission, setEmission] = useState(false);
  const [nouveau, setNouveau] = useState<McpTokenIssued | null>(null);
  const [outilsOuverts, setOutilsOuverts] = useState(false);

  const charger = useCallback(async () => {
    if (!orgId) return;
    setChargement(true);
    try {
      // Les deux en parallele : l'etat et la liste sont independants, et les enchainer
      // doublerait l'attente a l'ouverture de l'ecran.
      const [s, j, g] = await Promise.all([
        apiService.getMcpStatus(orgId),
        apiService.listMcpTokens(orgId),
        apiService.listMcpSessions(orgId),
      ]);
      setEtat(s);
      setJetons(j);
      setSessions(g);
      setEchec(false);
    } catch (err) {
      Logger.error('MCP: etat indisponible', err);
      setEchec(true);
    } finally {
      setChargement(false);
    }
  }, [orgId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const basculer = (c: McpCapability) =>
    setCapacites((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));

  const emettre = async () => {
    if (!orgId || emission || capacites.length === 0) return;
    setEmission(true);
    try {
      const r = await apiService.issueMcpToken(orgId, {
        label: label.trim() || undefined,
        capabilities: capacites,
      });
      // Le panneau s'ouvre AVANT le rechargement : si la liste echouait, le jeton
      // serait tout de meme a l'ecran. L'inverse le perdrait.
      setNouveau(r);
      setLabel('');
      await charger();
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { detail?: unknown } } };
      const detail = typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : null;
      Logger.error('MCP: emission impossible', err);
      toast.error(detail || String(t('emission.erreur')));
    } finally {
      setEmission(false);
    }
  };

  const revoquer = async (j: McpToken) => {
    if (!orgId) return;
    const nom = j.label || j.token_prefix;
    if (!window.confirm(String(t('jetons.confirmerRevocation', { nom })))) return;
    try {
      await apiService.revokeMcpToken(orgId, j.id);
      await charger();
      toast.success(String(t('jetons.revoque_fait')));
    } catch (err) {
      Logger.error('MCP: revocation impossible', err);
      toast.error(String(t('erreur')));
    }
  };

  /* Les sessions OAuth se revoquent comme les jetons, et l'effet est IMMEDIAT : les
     jetons sont opaques et relus en base a chaque appel, donc rien ne leur survit.
     C'est ce qu'un jeton auto-valide n'aurait pas permis de promettre. */
  const revoquerSession = async (g: McpGrant) => {
    if (!orgId) return;
    if (!window.confirm(String(t('sessions.confirmerRevocation', { client: g.client_hote })))) return;
    try {
      await apiService.revokeMcpSession(orgId, g.id);
      await charger();
      toast.success(String(t('sessions.revoquee')));
    } catch (err) {
      Logger.error('MCP: revocation de session impossible', err);
      toast.error(String(t('erreur')));
    }
  };

  if (!orgId) {
    return (
      <div className="p-6">
        <p className="text-sm text-text-secondary">{t('sansOrganisation')}</p>
      </div>
    );
  }

  const h24 = etat?.last_24h;

  return (
    <div className="p-6 max-w-5xl space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-text-primary">
          <ServerStackIcon className="w-6 h-6" strokeWidth={1.8} />
          {t('titre')}
        </h1>
        <p className="mt-1 text-sm text-text-secondary max-w-3xl">{t('sousTitre')}</p>
      </div>

      {chargement && <p className="text-sm text-text-tertiary">{t('chargement')}</p>}
      {echec && !chargement && <p className="text-sm text-red-500">{t('erreur')}</p>}

      {etat && (
        <>
          {/* L'adresse du serveur : la premiere chose dont on a besoin. */}
          <section className="border border-border-1 rounded-card p-4 bg-surface-1">
            <h2 className="text-sm font-medium text-text-primary">{t('adresse.titre')}</h2>
            <p className="mt-0.5 text-xs text-text-secondary">{t('adresse.aide')}</p>
            <div className="mt-2.5 flex items-center gap-2 flex-wrap">
              <code className="px-2.5 py-1.5 bg-surface-0 border border-border-1 rounded-input text-sm text-text-primary break-all">
                {etat.mcp_url}
              </code>
              <BoutonCopier
                valeur={etat.mcp_url}
                libelle={String(t('adresse.copier'))}
                fait={String(t('adresse.copie'))}
              />
            </div>
          </section>

          {/* L'activite. Les refus ont leur propre compteur : un jeton revoque ou des
              droits insuffisants ne sont pas une panne, et les melanger aux erreurs
              ferait chercher un incident la ou il n'y en a pas. */}
          <section className="border border-border-1 rounded-card p-4 bg-surface-1">
            <h2 className="text-sm font-medium text-text-primary">{t('etat.titre')}</h2>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-2xl font-semibold text-text-primary">{etat.jetons_actifs}</p>
                <p className="text-xs text-text-secondary">
                  {t('etat.jetons', { count: etat.jetons_actifs })}
                </p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-text-primary">{h24?.total ?? 0}</p>
                <p className="text-xs text-text-secondary">{t('etat.appels')}</p>
              </div>
              <div>
                <p className={`text-2xl font-semibold ${(h24?.errors ?? 0) > 0 ? 'text-amber-500' : 'text-text-primary'}`}>
                  {h24?.errors ?? 0}
                </p>
                <p className="text-xs text-text-secondary">{t('etat.erreurs')}</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-text-primary">{h24?.unauthorized ?? 0}</p>
                <p className="text-xs text-text-secondary">{t('etat.refus')}</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setOutilsOuverts((v) => !v)}
              className="mt-3 inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary transition-colors"
            >
              {outilsOuverts ? (
                <ChevronDownIcon className="w-3.5 h-3.5" strokeWidth={2} />
              ) : (
                <ChevronRightIcon className="w-3.5 h-3.5" strokeWidth={2} />
              )}
              {etat.outils_disponibles.length}{' '}
              {t('etat.outils', { count: etat.outils_disponibles.length })}
            </button>
            {outilsOuverts && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {etat.outils_disponibles.map((o) => (
                  <code
                    key={o}
                    className="px-1.5 py-0.5 bg-surface-2 border border-border-1 rounded text-xs text-text-secondary"
                  >
                    {o}
                  </code>
                ))}
              </div>
            )}
          </section>

          {/* L'emission. `destroy` n'est pas propose, et l'ecran dit POURQUOI. */}
          <section className="border border-border-1 rounded-card p-4 bg-surface-1">
            <h2 className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
              <KeyIcon className="w-4 h-4" strokeWidth={1.8} />
              {t('emission.titre')}
            </h2>

            <label className="block mt-3 text-xs text-text-secondary" htmlFor="mcp-label">
              {t('emission.label')}
            </label>
            <input
              id="mcp-label"
              type="text"
              value={label}
              maxLength={100}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={String(t('emission.labelAide'))}
              className="mt-1 w-full sm:max-w-md px-2.5 py-1.5 bg-surface-0 border border-border-1 rounded-input text-sm text-text-primary placeholder-text-quaternary focus:outline-none focus:border-accent"
            />

            <p className="mt-3 text-xs text-text-secondary">{t('emission.droits')}</p>
            <div className="mt-1.5 space-y-1.5">
              {CAPACITES.map((c) => (
                <label key={c} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={capacites.includes(c)}
                    onChange={() => basculer(c)}
                    className="mt-0.5 accent-accent"
                  />
                  <span className="text-sm text-text-primary">
                    {t(`emission.${c}`)}
                    <span className="block text-xs text-text-tertiary">{t(`emission.${c}Aide`)}</span>
                  </span>
                </label>
              ))}
            </div>

            <p className="mt-2.5 text-xs text-text-tertiary">{t('emission.pasDeDestruction')}</p>

            <button
              type="button"
              onClick={emettre}
              disabled={emission || capacites.length === 0}
              className="mt-3 px-4 py-2 bg-accent text-white rounded-button text-sm font-medium hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {emission ? t('emission.enCours') : t('emission.emettre')}
            </button>
          </section>

          {/* Les jetons. Les revoques restent listes : `revoked_at` est la trace. */}
          <section className="border border-border-1 rounded-card p-4 bg-surface-1">
            <h2 className="text-sm font-medium text-text-primary">{t('jetons.titre')}</h2>
            <p className="mt-0.5 text-xs text-text-secondary">{t('jetons.aide')}</p>

            {jetons.length === 0 ? (
              <p className="mt-3 text-sm text-text-tertiary">{t('jetons.aucun')}</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-text-tertiary border-b border-border-1">
                      <th className="pb-2 pr-3 font-medium">{t('jetons.colLabel')}</th>
                      <th className="pb-2 pr-3 font-medium">{t('jetons.colDroits')}</th>
                      <th className="pb-2 pr-3 font-medium">{t('jetons.colPrefixe')}</th>
                      <th className="pb-2 pr-3 font-medium">{t('jetons.colCree')}</th>
                      <th className="pb-2 pr-3 font-medium">{t('jetons.colVu')}</th>
                      <th className="pb-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {jetons.map((j) => {
                      const mort = !!j.revoked_at;
                      return (
                        <tr
                          key={j.id}
                          className={`border-b border-border-1 last:border-0 ${mort ? 'opacity-50' : ''}`}
                        >
                          <td className="py-2 pr-3 text-text-primary">
                            {j.label || <span className="text-text-tertiary">—</span>}
                            {mort && (
                              <span className="ml-1.5 text-xs text-text-tertiary">
                                ({t('jetons.revoque')})
                              </span>
                            )}
                            <span className="block text-xs text-text-tertiary">
                              {j.project_id ? t('jetons.porteeProjet') : t('jetons.portee')}
                            </span>
                          </td>
                          <td className="py-2 pr-3">
                            <span className="text-xs text-text-secondary">
                              {j.capabilities.join(' · ')}
                            </span>
                          </td>
                          <td className="py-2 pr-3">
                            <code className="text-xs text-text-secondary">{j.token_prefix}</code>
                          </td>
                          <td className="py-2 pr-3 text-xs text-text-secondary">
                            {new Date(j.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-2 pr-3 text-xs text-text-secondary">
                            {j.last_seen_at
                              ? new Date(j.last_seen_at).toLocaleString()
                              : t('jetons.jamaisVu')}
                          </td>
                          <td className="py-2 text-right">
                            {!mort && (
                              <button
                                type="button"
                                onClick={() => void revoquer(j)}
                                className="inline-flex items-center gap-1 text-xs text-text-tertiary hover:text-red-500 transition-colors"
                              >
                                <TrashIcon className="w-3.5 h-3.5" strokeWidth={1.8} />
                                {t('jetons.revoquer')}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Les sessions OAuth. SANS cette section, un proprietaire qui revoque tous
              ses jetons croit avoir coupe l'acces alors qu'il n'en a coupe qu'une
              moitie -- les deux chemins d'authentification cohabitent en permanence,
              ce n'est pas une transition. */}
          <section className="border border-border-1 rounded-card p-4 bg-surface-1">
            <h2 className="text-sm font-medium text-text-primary">{t('sessions.titre')}</h2>
            <p className="mt-0.5 text-xs text-text-secondary">{t('sessions.aide')}</p>

            {sessions.length === 0 ? (
              <p className="mt-3 text-sm text-text-tertiary">{t('sessions.aucune')}</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-text-tertiary border-b border-border-1">
                      <th className="pb-2 pr-3 font-medium">{t('sessions.colClient')}</th>
                      <th className="pb-2 pr-3 font-medium">{t('sessions.colPorteur')}</th>
                      <th className="pb-2 pr-3 font-medium">{t('sessions.colDroits')}</th>
                      <th className="pb-2 pr-3 font-medium">{t('sessions.colCree')}</th>
                      <th className="pb-2 pr-3 font-medium">{t('sessions.colVu')}</th>
                      <th className="pb-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((g) => (
                      <tr key={g.id} className="border-b border-border-1 last:border-0">
                        <td className="py-2 pr-3 text-text-primary">{g.client_hote}</td>
                        <td className="py-2 pr-3 text-xs text-text-secondary">
                          {g.porteur || <span className="text-text-tertiary">—</span>}
                        </td>
                        <td className="py-2 pr-3">
                          <span className="text-xs text-text-secondary">
                            {g.capacites.join(' · ')}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-xs text-text-secondary">
                          {new Date(g.cree_le).toLocaleDateString()}
                        </td>
                        <td className="py-2 pr-3 text-xs text-text-secondary">
                          {g.vu_le
                            ? new Date(g.vu_le).toLocaleString()
                            : t('sessions.jamaisVu')}
                        </td>
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            onClick={() => void revoquerSession(g)}
                            className="inline-flex items-center gap-1 text-xs text-text-tertiary hover:text-red-500 transition-colors"
                          >
                            <TrashIcon className="w-3.5 h-3.5" strokeWidth={1.8} />
                            {t('sessions.revoquer')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* L'activite detaillee : c'est elle qui evite un aller-retour de support
              quand un agent se fait refuser et que personne ne sait pourquoi. */}
          <section className="border border-border-1 rounded-card p-4 bg-surface-1">
            <h2 className="text-sm font-medium text-text-primary">{t('evenements.titre')}</h2>
            {etat.recent_events.length === 0 ? (
              <p className="mt-2 text-sm text-text-tertiary">{t('evenements.aucun')}</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-text-tertiary border-b border-border-1">
                      <th className="pb-2 pr-3 font-medium">{t('evenements.colHeure')}</th>
                      <th className="pb-2 pr-3 font-medium">{t('evenements.colMethode')}</th>
                      <th className="pb-2 pr-3 font-medium">{t('evenements.colOutil')}</th>
                      <th className="pb-2 pr-3 font-medium">{t('evenements.colIssue')}</th>
                      <th className="pb-2 font-medium">{t('evenements.colDuree')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {etat.recent_events.map((e) => (
                      <tr key={e.id} className="border-b border-border-1 last:border-0">
                        <td className="py-1.5 pr-3 text-xs text-text-secondary whitespace-nowrap">
                          {new Date(e.received_at).toLocaleTimeString()}
                        </td>
                        <td className="py-1.5 pr-3 text-xs text-text-secondary">{e.method}</td>
                        <td className="py-1.5 pr-3 text-xs text-text-secondary">
                          {e.tool_name || '—'}
                        </td>
                        <td className="py-1.5 pr-3 text-xs">
                          <span
                            className={
                              e.status_code >= 400 ? 'text-amber-500' : 'text-text-secondary'
                            }
                          >
                            {e.outcome} · {e.status_code}
                          </span>
                          {e.detail && (
                            <span className="block text-text-tertiary">{e.detail}</span>
                          )}
                        </td>
                        <td className="py-1.5 text-xs text-text-secondary whitespace-nowrap">
                          {e.duration_ms != null ? `${e.duration_ms} ms` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {/* LE JETON, UNE SEULE FOIS. Un panneau modal et non une ligne de liste : il n'y
          a aucun moyen de le relire, donc l'ecran doit barrer le chemin plutot que
          proposer une information de plus. */}
      {nouveau && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-xl bg-surface-1 border border-border-1 rounded-card p-5">
            <h3 className="flex items-center gap-1.5 text-base font-semibold text-text-primary">
              <KeyIcon className="w-5 h-5" strokeWidth={1.8} />
              {t('nouveau.titre')}
            </h3>
            <p className="mt-1.5 text-sm text-amber-600 dark:text-amber-500">
              {t('nouveau.avertissement')}
            </p>

            <code className="mt-3 block px-3 py-2.5 bg-surface-0 border border-border-1 rounded-input text-sm text-text-primary break-all select-all">
              {nouveau.token}
            </code>

            <div className="mt-3">
              <BoutonCopier
                valeur={nouveau.token}
                libelle={String(t('nouveau.copier'))}
                fait={String(t('nouveau.copie'))}
                large
              />
            </div>

            <p className="mt-4 text-xs text-text-secondary">{t('nouveau.config')}</p>
            <pre className="mt-1 px-3 py-2.5 bg-surface-0 border border-border-1 rounded-input text-xs text-text-secondary overflow-x-auto">
{`{
  "mcpServers": {
    "nubiecloud": {
      "type": "http",
      "url": "${nouveau.mcp_url}",
      "headers": { "Authorization": "Bearer ${nouveau.token}" }
    }
  }
}`}
            </pre>

            <button
              type="button"
              onClick={() => setNouveau(null)}
              className="mt-4 w-full px-4 py-2.5 bg-surface-2 text-text-primary border border-border-1 rounded-button text-sm hover:border-border-2 transition-colors"
            >
              {t('nouveau.ferme')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default McpServerPage;
